import React, { useState, useEffect, useMemo } from 'react';
import FormInput from '../common/FormInput';
import { useCrudOperations } from '../Hooks/useCrudOperations';
import { validatePostCode, validateProjectName } from '../../utils/validation';
import { API_URL, authHeaders } from '../../utils/api';
import { invalidateGlobalCache } from '../../hooks/useApiCache';

function UpdateProject({ project, builders = [], users: usersProp = [], setProjects, projects, onClose, onSuccess, initialTab }) {
  // Lokal users-state så nyskapade kunder kan läggas in direkt i listan
  // utan att behöva vänta på en re-fetch från parent.
  const [users, setUsers] = useState(usersProp);
  useEffect(() => { setUsers(usersProp); }, [usersProp]);

  const [values, setValues] = useState({
    projectId: project?.projectId || '',
    projectYear: project?.projectYear || '',
    projectDescription: project?.projectDescription || '',
    projectType: project?.projectType || '',
    address: project?.address || '',
    postCode: project?.postCode || '',
    city: project?.city || '',
    propertyDesignation: project?.propertyDesignation || '',
    customer: project?.customer?._id || '',
    additionalCustomers: Array.isArray(project?.additionalCustomers) 
      ? project.additionalCustomers.map(c => c?._id).filter(Boolean)
      : [],
    consultant: project?.consultant?._id || '',
    additionalConsultants: Array.isArray(project?.additionalConsultants)
      ? project.additionalConsultants.map(c => c?._id).filter(Boolean)
      : [],
    architect: project?.architect?._id || '',
    additionalArchitects: Array.isArray(project?.additionalArchitects)
      ? project.additionalArchitects.map(a => a?._id).filter(Boolean)
      : [],
    builder: project?.builder?._id || '',
    builderContactPerson: project?.builderContactPerson?._id || '',
    subcontractors: Array.isArray(project?.subcontractors)
      ? project.subcontractors.map(sc => sc?._id).filter(Boolean)
      : [],
    projectors: Array.isArray(project?.projectors)
      ? project.projectors.map(p => p?._id).filter(Boolean)
      : []
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [, setErrorMessage] = useState('');

  const { updateItem } = useCrudOperations(`${API_URL}/api/projects`, setProjects, projects);

  // State för extra kunder
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  // State för inline-skapande av ny ytterligare kund
  const [showCreateCustomerForm, setShowCreateCustomerForm] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [createCustomerError, setCreateCustomerError] = useState(null);
  const [createCustomerSuccess, setCreateCustomerSuccess] = useState(null);

  // State för huvudkund
  const [mainCustomerSearch, setMainCustomerSearch] = useState('');
  const [showMainCustomerDropdown, setShowMainCustomerDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // State för UE-sökning
  const [subcontractorSearch, setSubcontractorSearch] = useState('');
  const [showSubcontractorDropdown, setShowSubcontractorDropdown] = useState(false);

  // State för projektör-sökning
  const [projectorSearch, setProjectorSearch] = useState('');
  const [showProjectorDropdown, setShowProjectorDropdown] = useState(false);

  // State för byggkonsult-sök
  const [consultantSearch, setConsultantSearch] = useState('');
  const [showConsultantDropdown, setShowConsultantDropdown] = useState(false);

  // State för inredare-sök
  const [architectSearch, setArchitectSearch] = useState('');
  const [showArchitectDropdown, setShowArchitectDropdown] = useState(false);

  // Lägg till dessa state-variabler i komponenten
  const [materialSuppliers, setMaterialSuppliers] = useState(project.materialSuppliers || []);
  const [suppliersList, setSuppliersList] = useState([]);
  const [materialSupplierUsers, setMaterialSupplierUsers] = useState([]);
  const [materialSupplierSearchTerm, ] = useState('');
  const [, setFilteredSuppliers] = useState([]);

  // 1. Lägg till state för aktiv tab
  const [activeTab, setActiveTab] = useState(initialTab || 'project'); // 'project' eller 'team'

  // Materialleverantörer: sök och välj person direkt (ingen "lägg till"-knapp)
  const [materialSupplierPersonSearch, setMaterialSupplierPersonSearch] = useState('');
  const [showMaterialSupplierPersonDropdown, setShowMaterialSupplierPersonDropdown] = useState(false);

  // Filtrera kunder baserat på sökning för extrakunder
  const filteredCustomers = useMemo(() => {
    return users
      .filter(user => 
        user.role === 'kund' && 
        user._id !== values.customer &&
        !values.additionalCustomers.includes(user._id) &&
        (user.firstName.toLowerCase().includes(customerSearch.toLowerCase()) ||
         user.lastName.toLowerCase().includes(customerSearch.toLowerCase()))
      )
      .slice(0, 10); // Visa max 10 resultat
  }, [users, customerSearch, values.customer, values.additionalCustomers]);

  // Filtrera kunder baserat på sökning för huvudkund
  const filteredMainCustomers = useMemo(() => {
    return users
      .filter(user => 
        user.role === 'kund' && 
        !values.additionalCustomers.includes(user._id) && 
        (user.firstName.toLowerCase().includes(mainCustomerSearch.toLowerCase()) ||
         user.lastName.toLowerCase().includes(mainCustomerSearch.toLowerCase()))
      )
      .slice(0, 10); // Visa max 10 resultat
  }, [users, mainCustomerSearch, values.additionalCustomers]);

  // Filtrera byggare som är kopplade till det valda byggföretaget
  const filteredBuilderContacts = useMemo(() => {
    if (!values.builder) return [];
    
    // Hitta den valda byggaren
    const selectedBuilder = builders.find(b => b._id === values.builder);
    
    // Samla kontaktpersoner från båda källor
    const contactPersons = [];
    
    // 1. Från användare som är kopplade till byggaren
    const userContacts = users.filter(user => {
      if (user.role !== 'byggare' && user.role !== 'admin') return false;
      
      // Hantera både fall där builder är ett ID och där det är ett objekt
      if (typeof user.builder === 'string') {
        return user.builder === values.builder;
      } else if (user.builder && user.builder._id) {
        return user.builder._id === values.builder;
      }
      
      return false;
    });
    
    contactPersons.push(...userContacts);
    
    // 2. Från byggföretagets contactPerson och additionalContactPersons fält
    if (selectedBuilder) {
      // Lägg till huvudkontaktperson om den finns och inte redan är tillagd
      if (selectedBuilder.contactPerson) {
        const contactPersonId = typeof selectedBuilder.contactPerson === 'object' 
          ? selectedBuilder.contactPerson._id 
          : selectedBuilder.contactPerson;
        
        if (!contactPersons.some(u => u._id === contactPersonId)) {
          const contactUser = users.find(u => u._id === contactPersonId);
          if (contactUser) {
            contactPersons.push(contactUser);
          }
        }
      }
      
      // Lägg till ytterligare kontaktpersoner om de finns
      if (Array.isArray(selectedBuilder.additionalContactPersons)) {
        selectedBuilder.additionalContactPersons.forEach(contactId => {
          // Kontrollera om ID:t är ett objekt eller en sträng
          const contactPersonId = typeof contactId === 'object' ? contactId._id : contactId;
          
          // Lägg till om den inte redan finns
          if (!contactPersons.some(u => u._id === contactPersonId)) {
            const contactUser = users.find(u => u._id === contactPersonId);
            if (contactUser) {
              contactPersons.push(contactUser);
            }
          }
        });
      }
    }
    
    // Om det bara finns en kontaktperson, välj den automatiskt
    if (contactPersons.length === 1 && values.builder && !values.builderContactPerson) {
      // Sätt timeout för att undvika problem med renderingsordning
      setTimeout(() => {
        setValues(prev => ({
          ...prev,
          builderContactPerson: contactPersons[0]._id
        }));
      }, 0);
    }
    
    return contactPersons;
  }, [users, values.builder, builders, values.builderContactPerson]);

  // Filtrera UE baserat på sökning
  const filteredSubcontractors = useMemo(() => {
    return users
      .filter(user => 
        user.role === 'UE' && 
        !values.subcontractors.includes(user._id) &&
        ((user.firstName && user.firstName.toLowerCase().includes(subcontractorSearch.toLowerCase())) ||
         (user.lastName && user.lastName.toLowerCase().includes(subcontractorSearch.toLowerCase())) ||
         (user.companyName && user.companyName.toLowerCase().includes(subcontractorSearch.toLowerCase())))
      )
      .slice(0, 10); // Visa max 10 resultat
  }, [users, subcontractorSearch, values.subcontractors]);

  // Filtrera projektörer baserat på sökning
  const filteredProjectors = useMemo(() => {
    return users
      .filter(user =>
        user.role === 'projektor' &&
        !values.projectors.includes(user._id) &&
        ((user.firstName && user.firstName.toLowerCase().includes(projectorSearch.toLowerCase())) ||
         (user.lastName && user.lastName.toLowerCase().includes(projectorSearch.toLowerCase())) ||
         (user.companyName && user.companyName.toLowerCase().includes(projectorSearch.toLowerCase())))
      )
      .slice(0, 10); // Visa max 10 resultat
  }, [users, projectorSearch, values.projectors]);

  // Filtrera byggkonsulter baserat på sökning
  const filteredConsultants = useMemo(() => {
    return users
      .filter(user => 
        user.role === 'byggkonsult' && 
        user._id !== values.consultant &&
        !values.additionalConsultants.includes(user._id) &&
        (user.firstName.toLowerCase().includes(consultantSearch.toLowerCase()) ||
         user.lastName.toLowerCase().includes(consultantSearch.toLowerCase()))
      )
      .slice(0, 10); // Visa max 10 resultat
  }, [users, consultantSearch, values.consultant, values.additionalConsultants]);

  // Filtrera inredare baserat på sökning
  const filteredArchitects = useMemo(() => {
    return users
      .filter(user => 
        user.role === 'inredningsarkitekt' && 
        user._id !== values.architect &&
        !values.additionalArchitects.includes(user._id) &&
        (user.firstName.toLowerCase().includes(architectSearch.toLowerCase()) ||
         user.lastName.toLowerCase().includes(architectSearch.toLowerCase()))
      )
      .slice(0, 10); // Visa max 10 resultat
  }, [users, architectSearch, values.architect, values.additionalArchitects]);

  // Hämta alla UE för att visa i listan
  const projectSubcontractors = useMemo(() => {
    return values.subcontractors.map(id =>
      users.find(user => user._id === id)
    ).filter(Boolean);
  }, [users, values.subcontractors]);

  // Hämta alla projektörer för att visa i listan
  const projectProjectors = useMemo(() => {
    return values.projectors.map(id =>
      users.find(user => user._id === id)
    ).filter(Boolean);
  }, [users, values.projectors]);

  // Hämta alla byggkonsulter för att visa i listan
  const projectConsultants = useMemo(() => {
    return values.additionalConsultants.map(id => 
      users.find(user => user._id === id)
    ).filter(Boolean);
  }, [users, values.additionalConsultants]);

  // Hämta alla inredare för att visa i listan
  const projectArchitects = useMemo(() => {
    return values.additionalArchitects.map(id => 
      users.find(user => user._id === id)
    ).filter(Boolean);
  }, [users, values.additionalArchitects]);

  useEffect(() => {
    if (project) {
      setValues({
        projectId: project.projectId || '',
        projectYear: project.projectYear || '',
        projectDescription: project.projectDescription || '',
        projectType: project.projectType || 'Renovering',
        address: project.address || '',
        postCode: project.postCode || '',
        city: project.city || '',
        propertyDesignation: project.propertyDesignation || '',
        customer: project.customer?._id || '',
        additionalCustomers: Array.isArray(project.additionalCustomers)
          ? project.additionalCustomers.map(c => c?._id).filter(Boolean)
          : [],
        consultant: project.consultant?._id || '',
        architect: project.architect?._id || '',
        builder: project.builder?._id || '',
        builderContactPerson: project.builderContactPerson?._id || '',
        subcontractors: Array.isArray(project.subcontractors)
          ? project.subcontractors.map(sc => sc?._id).filter(Boolean)
          : [],
        projectors: Array.isArray(project.projectors)
          ? project.projectors.map(p => p?._id).filter(Boolean)
          : [],
        additionalConsultants: Array.isArray(project.additionalConsultants)
          ? project.additionalConsultants.map(c => c?._id).filter(Boolean)
          : [],
        additionalArchitects: Array.isArray(project.additionalArchitects)
          ? project.additionalArchitects.map(a => a?._id).filter(Boolean)
          : []
      });

      // Sätt selectedCustomer med customer från projektet
      if (project.customer) {
        setSelectedCustomer(project.customer);
        setMainCustomerSearch(`${project.customer.firstName} ${project.customer.lastName}`);
      }

      // Lägg till: mappa om materialSuppliers till rätt format för state
      if (
        Array.isArray(project.materialSuppliers) &&
        materialSupplierUsers.length > 0 &&
        suppliersList.length > 0
      ) {
        const mapped = project.materialSuppliers
          .map(ms => {
            let person = ms.contactPerson;
            if (typeof person === 'string') {
              person = materialSupplierUsers.find(u => u._id === person);
            }
            if (!person) return null;
            let supplier = ms.supplier;
            if (typeof supplier === 'string') {
              supplier = suppliersList.find(s => s._id === supplier);
            }
            return { ...person, supplier };
          })
          .filter(Boolean);
        setMaterialSuppliers(mapped);
      }
    }
  }, [project, materialSupplierUsers, suppliersList]);

  // Lägg till useEffect för att hämta leverantörer och materialleverantörsanvändare
  useEffect(() => {
    const fetchMaterialSupplierData = async () => {
      try {
        const [suppliersResponse, usersResponse] = await Promise.all([
          fetch(`${API_URL}/suppliers`, { headers: authHeaders() }),
          fetch(`${API_URL}/suppliers/material-suppliers/users`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            }
          })
        ]);

        if (suppliersResponse.ok && usersResponse.ok) {
          const [suppliersData, usersData] = await Promise.all([
            suppliersResponse.json(),
            usersResponse.json()
          ]);
          
          setSuppliersList(suppliersData);
          setMaterialSupplierUsers(usersData);
        }
      } catch (error) {
        console.error('Fel vid hämtning av leverantörsdata:', error);
      }
    };

    fetchMaterialSupplierData();
  }, []);

  // Lägg till useEffect för att filtrera leverantörer baserat på sökning
  useEffect(() => {
    if (materialSupplierSearchTerm) {
      const filtered = suppliersList.filter(supplier => 
        supplier.companyName.toLowerCase().includes(materialSupplierSearchTerm.toLowerCase())
      );
      setFilteredSuppliers(filtered);
    } else {
      setFilteredSuppliers([]);
    }
  }, [materialSupplierSearchTerm, suppliersList]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'postCode') {
      const validation = validatePostCode(value);
      if (validation.isValid) {
        setValues(prev => ({ ...prev, postCode: validation.formattedValue }));
        setErrors(prev => ({ ...prev, postCode: undefined }));
      } else {
        setValues(prev => ({ ...prev, postCode: value }));
        setErrors(prev => ({ ...prev, postCode: validation.message }));
      }
    } else if (name === 'projectId') {
      const validation = validateProjectName(value);
      if (validation.isValid) {
        setValues(prev => ({ ...prev, projectId: value }));
        setErrors(prev => ({ ...prev, projectId: undefined }));
      } else {
        setValues(prev => ({ ...prev, projectId: value }));
        setErrors(prev => ({ ...prev, projectId: validation.message }));
      }
    } else if (name === 'builder') {
      setValues(prev => ({ 
        ...prev, 
        [name]: value || null,
        builderContactPerson: '' // Återställ kontaktperson när byggföretaget ändras
      }));
    } else if (name === 'builderContactPerson') {
      setValues(prev => ({ ...prev, [name]: value || null }));
    } else if (['architect', 'consultant'].includes(name)) {
      setValues(prev => ({ ...prev, [name]: value || null }));
    } else {
      setValues(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handle click outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCustomerDropdown && !event.target.closest('.customer-dropdown-container')) {
        setShowCustomerDropdown(false);
      }
      if (showMainCustomerDropdown && !event.target.closest('.main-customer-dropdown-container')) {
        setShowMainCustomerDropdown(false);
      }
      if (showSubcontractorDropdown && !event.target.closest('.subcontractor-search')) {
        setShowSubcontractorDropdown(false);
      }
      if (showProjectorDropdown && !event.target.closest('.projector-search')) {
        setShowProjectorDropdown(false);
      }
      if (showConsultantDropdown && !event.target.closest('.consultant-dropdown-container')) {
        setShowConsultantDropdown(false);
      }
      if (showArchitectDropdown && !event.target.closest('.architect-dropdown-container')) {
        setShowArchitectDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // showProjectorDropdown saknades här. Effekten läser den (se ovan), men utan
    // den i listan registrerades lyssnaren aldrig om när bara projektör-
    // rullgardinen öppnades — den fångade då ett inaktuellt false och stängdes
    // inte vid klick utanför.
  }, [showCustomerDropdown, showMainCustomerDropdown, showSubcontractorDropdown, showProjectorDropdown, showConsultantDropdown, showArchitectDropdown]);

  // Hantera val av huvudkund
  const handleSelectMainCustomer = (customer) => {
    setValues({ ...values, customer: customer._id });
    setSelectedCustomer(customer);
    setMainCustomerSearch(`${customer.firstName} ${customer.lastName}`);
    setShowMainCustomerDropdown(false);
  };

  const handleClearMainCustomer = () => {
    setValues({ ...values, customer: '' });
    setSelectedCustomer(null);
    setMainCustomerSearch('');
  };

  const handleSearchMainCustomer = (e) => {
    setMainCustomerSearch(e.target.value);
    setShowMainCustomerDropdown(true);
  };

  // Hantera val av ytterligare kund
  const handleSelectAdditionalCustomer = (customer) => {
    const updatedAdditionalCustomers = [...values.additionalCustomers, customer._id];
    setValues({ ...values, additionalCustomers: updatedAdditionalCustomers });
    setCustomerSearch('');
    setShowCustomerDropdown(false);
  };

  // Hantera borttagning av ytterligare kund
  const handleRemoveAdditionalCustomer = (customerId) => {
    const updatedAdditionalCustomers = values.additionalCustomers.filter(
      (id) => id !== customerId
    );
    setValues({ ...values, additionalCustomers: updatedAdditionalCustomers });
  };

  // Skapa ny ytterligare kund inline (skickar välkomstmail) och lägg till i listan
  const handleCreateAdditionalCustomer = async () => {
    setCreateCustomerError(null);
    setCreateCustomerSuccess(null);
    const { firstName, lastName, email, phone } = newCustomer;
    if (!firstName.trim() || !email.trim()) {
      setCreateCustomerError('Förnamn och e-post krävs');
      return;
    }
    try {
      setCreatingCustomer(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/projects/${project._id}/create-additional-customer`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstName, lastName, email, phone })
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.message || `HTTP ${res.status}`);
      }
      const newUser = json.customer;

      // Lägg till i lokala users-listan så Ytterligare kunder-raden hittar namnet
      setUsers(prev => {
        if (prev.some(u => u._id === newUser._id)) return prev;
        return [...prev, newUser];
      });
      // Lägg till i values.additionalCustomers (om inte redan)
      setValues(prev => {
        if (prev.additionalCustomers.includes(newUser._id)) return prev;
        return { ...prev, additionalCustomers: [...prev.additionalCustomers, newUser._id] };
      });
      // Återställ formulär
      setNewCustomer({ firstName: '', lastName: '', email: '', phone: '' });
      setShowCreateCustomerForm(false);
      setCreateCustomerSuccess(json.message || 'Kund tillagd');
      // Auto-rensa success-meddelande
      setTimeout(() => setCreateCustomerSuccess(null), 4000);
    } catch (err) {
      setCreateCustomerError(err.message || 'Kunde inte skapa kund');
    } finally {
      setCreatingCustomer(false);
    }
  };

  // Kontrollera om en kund är associerad med befintliga projekt
  const isCustomerAssociatedWithProjects = (customerId) => {
    if (!project || !projects) return false;
    
    return projects.some(p => 
      (p._id !== project._id) && // Exkludera det aktuella projektet
      (p.customer?._id === customerId || 
      (Array.isArray(p.additionalCustomers) && 
       p.additionalCustomers.some(c => c?._id === customerId)))
    );
  };

  // Hantera val av underleverantörer
  const handleSelectSubcontractor = (subcontractor) => {
    setValues(prev => ({
      ...prev,
      subcontractors: [...prev.subcontractors, subcontractor._id]
    }));
    setSubcontractorSearch('');
    setShowSubcontractorDropdown(false);
  };

  // Hantera borttagning av underleverantör
  const handleRemoveSubcontractor = (subcontractorId) => {
    setValues(prev => ({
      ...prev,
      subcontractors: prev.subcontractors.filter(id => id !== subcontractorId)
    }));
  };

  // Hantera val av projektör
  const handleSelectProjector = (projector) => {
    setValues(prev => ({
      ...prev,
      projectors: [...prev.projectors, projector._id]
    }));
    setProjectorSearch('');
    setShowProjectorDropdown(false);
  };

  // Hantera borttagning av projektör
  const handleRemoveProjector = (projectorId) => {
    setValues(prev => ({
      ...prev,
      projectors: prev.projectors.filter(id => id !== projectorId)
    }));
  };

  // Hantera val av ytterligare byggkonsult
  const handleSelectAdditionalConsultant = (consultant) => {
    const updatedAdditionalConsultants = [...values.additionalConsultants, consultant._id];
    setValues({ ...values, additionalConsultants: updatedAdditionalConsultants });
    setConsultantSearch('');
    setShowConsultantDropdown(false);
  };

  // Hantera borttagning av ytterligare byggkonsult
  const handleRemoveAdditionalConsultant = (consultantId) => {
    const updatedAdditionalConsultants = values.additionalConsultants.filter(
      (id) => id !== consultantId
    );
    setValues({ ...values, additionalConsultants: updatedAdditionalConsultants });
  };

  // Hantera val av ytterligare inredare
  const handleSelectAdditionalArchitect = (architect) => {
    const updatedAdditionalArchitects = [...values.additionalArchitects, architect._id];
    setValues({ ...values, additionalArchitects: updatedAdditionalArchitects });
    setArchitectSearch('');
    setShowArchitectDropdown(false);
  };

  // Hantera borttagning av ytterligare inredare
  const handleRemoveAdditionalArchitect = (architectId) => {
    const updatedAdditionalArchitects = values.additionalArchitects.filter(
      (id) => id !== architectId
    );
    setValues({ ...values, additionalArchitects: updatedAdditionalArchitects });
  };





  // 2. Valideringsfunktioner per tab
  const validateProjectInfoTab = () => {
    const validationErrors = {};
    if (!values.projectId) validationErrors.projectId = 'Projektnamn är obligatoriskt';
    if (values.projectId && !validateProjectName(values.projectId).isValid) validationErrors.projectId = 'Projektnamnet är ogiltigt';
    if (!values.address) validationErrors.address = 'Adress är obligatoriskt';
    if (!values.postCode) validationErrors.postCode = 'Postnummer är obligatoriskt';
    if (values.postCode && !validatePostCode(values.postCode).isValid) validationErrors.postCode = 'Postnummer är ogiltigt';
    if (!values.city) validationErrors.city = 'Ort är obligatoriskt';
    if (!values.customer) validationErrors.customer = 'Kund är obligatoriskt';
    return validationErrors;
  };
  const validateTeamTab = () => {
    // Lägg till validering för team-fält om det behövs
    return {};
  };

  // 3. Hantera tabbyte med validering
  const handleTabChange = (tab) => {
    if (activeTab === 'project') {
      const errors = validateProjectInfoTab();
      if (Object.keys(errors).length > 0) {
        setErrors(errors);
        return;
      }
    }
    if (activeTab === 'team') {
      const errors = validateTeamTab();
      if (Object.keys(errors).length > 0) {
        setErrors(errors);
        return;
      }
    }
    setErrors({});
    setActiveTab(tab);
  };

  // 4. Uppdatera handleSubmit för att validera båda tabs
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errorsProject = validateProjectInfoTab();
    const errorsTeam = validateTeamTab();
    const allErrors = { ...errorsProject, ...errorsTeam };
    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      if (Object.keys(errorsProject).length > 0) setActiveTab('project');
      else setActiveTab('team');
      return;
    }
    setIsLoading(true);
    setErrorMessage('');
    try {
      // Korrigera materialleverantörer till rätt format
      const formattedMaterialSuppliers = materialSuppliers
        .filter(ms => ms && ms.supplier && ms.supplier._id && ms._id)
        .map(ms => ({
          supplier: ms.supplier._id,
          contactPerson: ms._id
        }));
      const projectData = {
        projectId: values.projectId,
        projectYear: values.projectYear || undefined,
        projectDescription: values.projectDescription,
        projectType: values.projectType,
        address: values.address,
        postCode: values.postCode,
        city: values.city,
        propertyDesignation: values.propertyDesignation,
        customer: values.customer,
        additionalCustomers: values.additionalCustomers || [],
        consultant: values.consultant || null,
        additionalConsultants: values.additionalConsultants || [],
        architect: values.architect || null,
        additionalArchitects: values.additionalArchitects || [],
        builder: values.builder || null,
        builderContactPerson: values.builderContactPerson || null,
        subcontractors: values.subcontractors || [],
        projectors: values.projectors || [],
        materialSuppliers: formattedMaterialSuppliers
      };
      await updateItem(project._id, projectData);
      
      // 🚀 CACHE FIX: Invalidera relevanta caches när projekt uppdateras
      invalidateGlobalCache([
        'myprojects_projects',          // MyProjects-komponenten
        'myassigned_projects',          // MyAssignedProjects-komponenten
        'all_projects',                 // Alla projekt
        'dashboard_projects'            // Dashboard-komponenten
      ]);

      // onSuccess stänger OCH hämtar om projekten. Workspace (Översikt,
      // Projekt) skickar den, men den anropades aldrig — modalen stängdes bara
      // och teamet stod kvar som före sparningen tills man laddade om sidan.
      if (onSuccess) onSuccess();
      else onClose();
    } catch (error) {
      console.error('Error updating project:', error);
      setErrorMessage('Kunde inte uppdatera projektet');
    } finally {
      setIsLoading(false);
    }
  };

  // Materialleverantörer: sök och välj person direkt (ingen "lägg till"-knapp)
  const filteredMaterialSupplierPersons = useMemo(() => {
    if (!materialSupplierPersonSearch) return [];
    return materialSupplierUsers.filter(user =>
      user.role === 'materialleverantör' &&
      !materialSuppliers.some(ms => ms._id === user._id) &&
      (
        (user.firstName && user.firstName.toLowerCase().includes(materialSupplierPersonSearch.toLowerCase())) ||
        (user.lastName && user.lastName.toLowerCase().includes(materialSupplierPersonSearch.toLowerCase())) ||
        (user.companyName && user.companyName.toLowerCase().includes(materialSupplierPersonSearch.toLowerCase())) ||
        (user.supplier && typeof user.supplier === 'object' && user.supplier.companyName && user.supplier.companyName.toLowerCase().includes(materialSupplierPersonSearch.toLowerCase()))
      )
    ).slice(0, 10);
  }, [materialSupplierUsers, materialSupplierPersonSearch, materialSuppliers]);

  const handleSelectMaterialSupplierPerson = (person) => {
    setMaterialSuppliers(prev => [...prev, person]);
    setMaterialSupplierPersonSearch('');
    setShowMaterialSupplierPersonDropdown(false);
  };

  const handleRemoveMaterialSupplierPerson = (personId) => {
    setMaterialSuppliers(prev => prev.filter(p => p._id !== personId));
  };

  // Hjälpfunktion för att hämta bolagsnamn
  const getCompanyName = (person) => {
    if (person.supplier && typeof person.supplier === 'object' && person.supplier.companyName) {
      return person.supplier.companyName;
    }
    return '';
  };

  // Hjälpfunktion för att hämta leverantörstyp
  const getSupplierType = (person) => {
    if (person.supplier && typeof person.supplier === 'object' && person.supplier.supplierType) {
      return person.supplier.supplierType;
    }
    return '';
  };

  return (
    <div className="p-6">
      {/* Tabs högst upp */}
      <div className="flex border-b mb-6">
        <button
          type="button"
          className={`px-4 py-2 font-medium focus:outline-none transition-colors duration-200 ${activeTab === 'project' ? 'border-b-2 border-primary text-primary' : 'text-gray-500 hover:text-primary'}`}
          onClick={() => handleTabChange('project')}
          aria-label="Projektinformation"
          tabIndex={0}
        >
          Projektinformation
        </button>
        <button
          type="button"
          className={`ml-4 px-4 py-2 font-medium focus:outline-none transition-colors duration-200 ${activeTab === 'team' ? 'border-b-2 border-primary text-primary' : 'text-gray-500 hover:text-primary'}`}
          onClick={() => handleTabChange('team')}
          aria-label="Team"
          tabIndex={0}
        >
          Team
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Projektinformation-tabben */}
        {activeTab === 'project' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Projekt ID *
              </label>
              <input
                type="text"
                name="projectId"
                value={values.projectId}
                onChange={handleChange}
                required
                placeholder="#12345"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  errors.projectId ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.projectId && (
                <p className="mt-1 text-sm text-red-600">{errors.projectId}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Projektår (SharePoint-mapp)
              </label>
              <input
                type="text"
                name="projectYear"
                value={values.projectYear}
                onChange={handleChange}
                placeholder={new Date().getFullYear().toString()}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                maxLength={4}
              />
              <p className="mt-1 text-xs text-gray-500">Vilket år projektet tillhör i SharePoint (t.ex. 2025, 2026)</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Projektbeskrivning
              </label>
              <input
                type="text"
                name="projectDescription"
                value={values.projectDescription}
                onChange={handleChange}
                placeholder="T.ex. Etapp 1, Övervåning"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500">Valfritt beskrivande namn för projektet</p>
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Projekttyp
              </label>
              <select
                name="projectType"
                value={values.projectType}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="Renovering">Renovering</option>
                <option value="Tillbyggnad">Tillbyggnad</option>
                <option value="Taklyft">Taklyft</option>
                <option value="Källare">Källare</option>
                <option value="Övrigt">Övrigt</option>
              </select>
            </div>
            <FormInput
              label="Adress"
              name="address"
              value={values.address}
              onChange={handleChange}
              required
              placeholder="Ange adress"
              error={errors.address}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Postnummer *
              </label>
              <input
                type="text"
                name="postCode"
                value={values.postCode}
                onChange={handleChange}
                required
                placeholder="123 45"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  errors.postCode ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.postCode && (
                <p className="mt-1 text-sm text-red-600">{errors.postCode}</p>
              )}
            </div>
            <FormInput
              label="Postort"
              name="city"
              value={values.city}
              onChange={handleChange}
              required
              placeholder="Ange postort"
              error={errors.city}
            />
            {/* Fastighetsbeteckning */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fastighetsbeteckning
              </label>
              <input
                type="text"
                name="propertyDesignation"
                value={values.propertyDesignation}
                onChange={handleChange}
                placeholder="T.ex. Faddergåvan 10, Södermalm 1:5"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500">
                Krävs för ROT-avdrag. Hittas i fastighetsregistret.
              </p>
            </div>
            {/* Huvudkund och ytterligare kund */}
            <div className="main-customer-dropdown-container">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Huvudkund *
              </label>
              <div className="relative">
                {selectedCustomer ? (
                  <div className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg border border-gray-300">
                    <span title={selectedCustomer.email}>{selectedCustomer.firstName} {selectedCustomer.lastName}</span>
                    <button
                      type="button"
                      onClick={handleClearMainCustomer}
                      className="text-gray-500 hover:text-red-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <input
                    type="text"
                    placeholder="Sök kund..."
                    value={mainCustomerSearch}
                    onChange={handleSearchMainCustomer}
                    onFocus={() => setShowMainCustomerDropdown(true)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                      errors.customer ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                )}
                {errors.customer && (
                  <p className="mt-1 text-sm text-red-600">{errors.customer}</p>
                )}
                
                {showMainCustomerDropdown && mainCustomerSearch && (
                  <div className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-lg border border-gray-200 max-h-60 overflow-auto">
                    {filteredMainCustomers.length > 0 ? (
                      filteredMainCustomers.map(user => (
                        <div
                          key={`main-customer-${user._id}`}
                          className="px-4 py-2 hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleSelectMainCustomer(user)}
                        >
                          <div className="flex flex-col">
                            <span className="font-medium flex items-center">
                              {user.firstName} {user.lastName}
                              {!isCustomerAssociatedWithProjects(user._id) && 
                                <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">Ny</span>
                              }
                            </span>
                            <span className="text-xs text-gray-500">{user.email}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-gray-500">
                        Inga träffar
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ytterligare kunder
              </label>
              
              <div className="customer-dropdown-container">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Sök kund..."
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      setShowCustomerDropdown(true);
                    }}
                    onFocus={() => setShowCustomerDropdown(true)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  
                  {showCustomerDropdown && customerSearch && (
                    <div className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-lg border border-gray-200 max-h-60 overflow-auto">
                      {filteredCustomers.length > 0 ? (
                        filteredCustomers.map(user => (
                          <div
                            key={`additional-customer-${user._id}`}
                            className="px-4 py-2 hover:bg-gray-50 cursor-pointer"
                            onClick={() => handleSelectAdditionalCustomer(user)}
                          >
                            <div className="flex flex-col">
                              <span className="font-medium flex items-center">
                                {user.firstName} {user.lastName}
                                {!isCustomerAssociatedWithProjects(user._id) &&
                                  <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">Ny</span>
                                }
                              </span>
                              <span className="text-xs text-gray-500">{user.email}</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-gray-500">
                          Inga träffar
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* "Lägg till ny"-knapp + inline-formulär */}
              <div className="mt-2">
                {!showCreateCustomerForm ? (
                  <button
                    type="button"
                    onClick={() => { setShowCreateCustomerForm(true); setCreateCustomerError(null); }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-leadme-pink hover:bg-leadme-pink-dark rounded-lg transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Lägg till ny kund
                  </button>
                ) : (
                  <div className="border border-leadme-pink/30 rounded-lg bg-leadme-pink/5 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-gray-800">Skapa ny kund</h4>
                      <button
                        type="button"
                        onClick={() => { setShowCreateCustomerForm(false); setCreateCustomerError(null); setNewCustomer({ firstName: '', lastName: '', email: '', phone: '' }); }}
                        className="text-gray-400 hover:text-gray-600"
                        title="Stäng"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <p className="text-xs text-gray-600">
                      Skapar en ny kund i systemet och lägger till på projektet. Välkomstmail med inloggningsuppgifter skickas automatiskt.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input
                        type="text"
                        placeholder="Förnamn *"
                        value={newCustomer.firstName}
                        onChange={(e) => setNewCustomer({ ...newCustomer, firstName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-leadme-pink/30 focus:border-leadme-pink"
                      />
                      <input
                        type="text"
                        placeholder="Efternamn"
                        value={newCustomer.lastName}
                        onChange={(e) => setNewCustomer({ ...newCustomer, lastName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-leadme-pink/30 focus:border-leadme-pink"
                      />
                      <input
                        type="email"
                        placeholder="E-post *"
                        value={newCustomer.email}
                        onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-leadme-pink/30 focus:border-leadme-pink"
                      />
                      <input
                        type="tel"
                        placeholder="Telefon"
                        value={newCustomer.phone}
                        onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-leadme-pink/30 focus:border-leadme-pink"
                      />
                    </div>
                    {createCustomerError && (
                      <div className="text-xs text-red-600 bg-red-50 px-2 py-1.5 rounded">{createCustomerError}</div>
                    )}
                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => { setShowCreateCustomerForm(false); setCreateCustomerError(null); setNewCustomer({ firstName: '', lastName: '', email: '', phone: '' }); }}
                        className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Avbryt
                      </button>
                      <button
                        type="button"
                        onClick={handleCreateAdditionalCustomer}
                        disabled={creatingCustomer}
                        className={`px-3 py-1.5 text-sm font-medium text-white rounded-lg ${
                          creatingCustomer ? 'bg-gray-300 cursor-not-allowed' : 'bg-leadme-pink hover:bg-leadme-pink-dark'
                        }`}
                      >
                        {creatingCustomer ? 'Skapar...' : 'Skapa & skicka välkomstmail'}
                      </button>
                    </div>
                  </div>
                )}
                {createCustomerSuccess && (
                  <div className="mt-2 text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-1.5 rounded">
                    ✓ {createCustomerSuccess}
                  </div>
                )}
              </div>

              {/* Lista över valda ytterligare kunder */}
              <div className="mt-2 space-y-1">
                {values.additionalCustomers.length > 0 && users.length > 0 ? (
                  values.additionalCustomers.map((customerId) => {
                    const customer = users.find((c) => c._id === customerId);
                    return customer ? (
                      <div key={customerId} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
                        <span title={customer.email}>{customer.firstName} {customer.lastName}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveAdditionalCustomer(customerId)}
                          className="text-gray-500 hover:text-red-600"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : null;
                  })
                ) : (
                  <p className="text-gray-500 text-sm mt-1">Inga ytterligare kunder tillagda</p>
                )}
              </div>
            </div>
          </>
        )}
        {/* Team-tabben */}
        {activeTab === 'team' && (
          <>
            {/* Byggkonsult */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Byggkonsult
              </label>
              <select
                name="consultant"
                value={values.consultant || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Välj byggkonsult</option>
                {users.filter(user => user.role === 'byggkonsult').map(user => (
                  <option key={user._id} value={user._id}>
                    {user.firstName} {user.lastName}
                  </option>
                ))}
              </select>
            </div>

            {/* Ytterligare byggkonsult - visas endast när huvudkonsult är vald */}
            {values.consultant && (
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ytterligare byggkonsulter
                </label>
                <div className="consultant-dropdown-container">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Sök byggkonsult..."
                      value={consultantSearch}
                      onChange={(e) => {
                        setConsultantSearch(e.target.value);
                        setShowConsultantDropdown(true);
                      }}
                      onFocus={() => setShowConsultantDropdown(true)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    {showConsultantDropdown && consultantSearch && (
                      <div className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-lg border border-gray-200 max-h-60 overflow-auto">
                        {filteredConsultants.length > 0 ? (
                          filteredConsultants.map(user => (
                            <div
                              key={`additional-consultant-${user._id}`}
                              className="px-4 py-2 hover:bg-gray-50 cursor-pointer"
                              onClick={() => handleSelectAdditionalConsultant(user)}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">{user.firstName} {user.lastName}</span>
                                <span className="text-xs text-gray-500">{user.email}</span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="px-4 py-2 text-gray-500">
                            Inga träffar
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {/* Lista över valda ytterligare byggkonsulter */}
                <div className="mt-2 space-y-1">
                  {projectConsultants.length > 0 ? (
                    projectConsultants.map((consultant) => (
                      <div key={consultant._id} className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded-lg border border-blue-100">
                        <span title={consultant.email}>{consultant.firstName} {consultant.lastName}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveAdditionalConsultant(consultant._id)}
                          className="text-gray-500 hover:text-red-600"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm mt-1">Inga ytterligare byggkonsulter tillagda</p>
                  )}
                </div>
              </div>
            )}

            {/* Inredningsarkitekt */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Inredningsarkitekt
              </label>
              <select
                name="architect"
                value={values.architect || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Välj inredningsarkitekt</option>
                {users.filter(user => user.role === 'inredningsarkitekt').map(user => (
                  <option key={user._id} value={user._id}>
                    {user.firstName} {user.lastName}
                  </option>
                ))}
              </select>
            </div>

            {/* Ytterligare inredningsarkitekt - visas endast när huvudarkitekt är vald */}
            {values.architect && (
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ytterligare inredningsarkitekter
                </label>
                <div className="architect-dropdown-container">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Sök inredningsarkitekt..."
                      value={architectSearch}
                      onChange={(e) => {
                        setArchitectSearch(e.target.value);
                        setShowArchitectDropdown(true);
                      }}
                      onFocus={() => setShowArchitectDropdown(true)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    {showArchitectDropdown && architectSearch && (
                      <div className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-lg border border-gray-200 max-h-60 overflow-auto">
                        {filteredArchitects.length > 0 ? (
                          filteredArchitects.map(user => (
                            <div
                              key={`additional-architect-${user._id}`}
                              className="px-4 py-2 hover:bg-gray-50 cursor-pointer"
                              onClick={() => handleSelectAdditionalArchitect(user)}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">{user.firstName} {user.lastName}</span>
                                <span className="text-xs text-gray-500">{user.email}</span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="px-4 py-2 text-gray-500">
                            Inga träffar
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {/* Lista över valda ytterligare inredningsarkitekter */}
                <div className="mt-2 space-y-1">
                  {projectArchitects.length > 0 ? (
                    projectArchitects.map((architect) => (
                      <div key={architect._id} className="flex items-center justify-between bg-purple-50 px-3 py-2 rounded-lg border border-purple-100">
                        <span title={architect.email}>{architect.firstName} {architect.lastName}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveAdditionalArchitect(architect._id)}
                          className="text-gray-500 hover:text-red-600"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm mt-1">Inga ytterligare inredningsarkitekter tillagda</p>
                  )}
                </div>
              </div>
            )}

            {/* Byggföretag */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Byggföretag
              </label>
              <select
                name="builder"
                value={values.builder || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Välj byggföretag</option>
                {builders.map(builder => (
                  <option key={builder._id} value={builder._id}>
                    {builder.companyName}
                  </option>
                ))}
              </select>
            </div>

            {/* Kontaktperson för byggföretag */}
            {values.builder && filteredBuilderContacts.length !== 1 && (
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Kontaktperson
                </label>
                <select
                  name="builderContactPerson"
                  value={values.builderContactPerson || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">Välj kontaktperson</option>
                  {filteredBuilderContacts.map(user => (
                    <option key={user._id} value={user._id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
                {filteredBuilderContacts.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">Välj en kontaktperson för detta byggföretag</p>
                )}
              </div>
            )}
            {values.builder && filteredBuilderContacts.length === 1 && (
              <div className="rounded-lg bg-blue-50 p-3 text-sm">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-blue-700">
                      Kontaktperson: <span className="font-semibold">{filteredBuilderContacts[0].firstName} {filteredBuilderContacts[0].lastName}</span> 
                      <span className="block text-xs mt-1">Det finns bara en kontaktperson för detta byggföretag.</span>
                    </p>
                  </div>
                </div>
              </div>
            )}
            {values.builder && filteredBuilderContacts.length === 0 && (
              <p className="text-xs text-amber-500 mt-1">Det finns inga kontaktpersoner för det valda byggföretaget</p>
            )}

            {/* Underleverantör */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Underleverantörer</label>
              <div className="relative subcontractor-search">
                <input
                  type="text"
                  placeholder="Sök underleverantör..."
                  value={subcontractorSearch}
                  onChange={(e) => {
                    setSubcontractorSearch(e.target.value);
                    setShowSubcontractorDropdown(true);
                  }}
                  onFocus={() => setShowSubcontractorDropdown(true)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                {/* Dropdown med sökresultat för UE */}
                {showSubcontractorDropdown && subcontractorSearch && (
                  <div className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-lg border border-gray-200 max-h-60 overflow-auto">
                    {filteredSubcontractors.length > 0 ? (
                      filteredSubcontractors.map(ue => (
                        <div
                          key={`subcontractor-${ue._id}`}
                          className="px-4 py-2 hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleSelectSubcontractor(ue)}
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{ue.firstName} {ue.lastName}</span>
                            <span className="text-xs text-gray-500">
                              {ue.companyName}
                              {ue.contractorType && ` (${ue.contractorType})`}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-gray-500">
                        Inga träffar
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Lista över valda UE */}
              <div className="mt-2 space-y-1">
                {projectSubcontractors.length > 0 ? (
                  projectSubcontractors.map(subcontractor => (
                    <div key={subcontractor._id} className="flex items-center justify-between bg-green-50 px-3 py-2 rounded-lg border border-green-100">
                      <div>
                        <span className="font-medium">{subcontractor.firstName} {subcontractor.lastName}</span>
                        <span className="text-xs text-gray-500 block">
                          {subcontractor.companyName}
                          {subcontractor.contractorType && ` (${subcontractor.contractorType})`}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="text-gray-500 hover:text-red-600"
                        onClick={() => handleRemoveSubcontractor(subcontractor._id)}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm mt-1">Inga underleverantörer tillagda</p>
                )}
              </div>
            </div>

            {/* Projektörer */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Projektörer</label>
              <div className="relative projector-search">
                <input
                  type="text"
                  placeholder="Sök projektör..."
                  value={projectorSearch}
                  onChange={(e) => {
                    setProjectorSearch(e.target.value);
                    setShowProjectorDropdown(true);
                  }}
                  onFocus={() => setShowProjectorDropdown(true)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                {/* Dropdown med sökresultat för projektörer */}
                {showProjectorDropdown && projectorSearch && (
                  <div className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-lg border border-gray-200 max-h-60 overflow-auto">
                    {filteredProjectors.length > 0 ? (
                      filteredProjectors.map(p => (
                        <div
                          key={`projector-${p._id}`}
                          className="px-4 py-2 hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleSelectProjector(p)}
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{p.firstName} {p.lastName}</span>
                            <span className="text-xs text-gray-500">
                              {p.companyName}
                              {p.projectorType && ` (${p.projectorType})`}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-gray-500">
                        Inga träffar
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Lista över valda projektörer */}
              <div className="mt-2 space-y-1">
                {projectProjectors.length > 0 ? (
                  projectProjectors.map(projector => (
                    <div key={projector._id} className="flex items-center justify-between bg-purple-50 px-3 py-2 rounded-lg border border-purple-100">
                      <div>
                        <span className="font-medium">{projector.firstName} {projector.lastName}</span>
                        <span className="text-xs text-gray-500 block">
                          {projector.companyName}
                          {projector.projectorType && ` (${projector.projectorType})`}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="text-gray-500 hover:text-red-600"
                        onClick={() => handleRemoveProjector(projector._id)}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm mt-1">Inga projektörer tillagda</p>
                )}
              </div>
            </div>

            {/* Materialleverantörer */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Materialleverantörer</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Sök materialleverantör (namn eller företag)..."
                  value={materialSupplierPersonSearch}
                  onChange={e => {
                    setMaterialSupplierPersonSearch(e.target.value);
                    setShowMaterialSupplierPersonDropdown(true);
                  }}
                  onFocus={() => setShowMaterialSupplierPersonDropdown(true)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                />
                {showMaterialSupplierPersonDropdown && materialSupplierPersonSearch && (
                  <div className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-lg border border-gray-200 max-h-60 overflow-auto">
                    {filteredMaterialSupplierPersons.length > 0 ? (
                      filteredMaterialSupplierPersons.map(person => {
                        const company = getCompanyName(person);
                        const supplierType = getSupplierType(person);
                        return (
                          <div
                            key={person._id}
                            className="px-4 py-2 hover:bg-gray-50 cursor-pointer"
                            onClick={() => handleSelectMaterialSupplierPerson(person)}
                          >
                            <div className="flex flex-col">
                              <span className="font-medium">{person.firstName} {person.lastName}</span>
                              <span className="text-xs text-gray-500">
                                {company}
                                {supplierType && ` (${supplierType})`}
                              </span>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="px-4 py-2 text-gray-500">Inga träffar</div>
                    )}
                  </div>
                )}
              </div>
              {/* Lista över valda materialleverantörer */}
              <div className="mt-2 space-y-1">
                {materialSuppliers.length > 0 ? (
                  materialSuppliers.filter(person => person && (person.firstName || person.lastName)).map(person => {
                    const company = getCompanyName(person);
                    const supplierType = getSupplierType(person);
                    return (
                      <div key={person._id} className="flex items-center justify-between bg-green-50 px-3 py-2 rounded-lg border border-green-100">
                        <div>
                          <span className="font-medium">{person.firstName} {person.lastName}</span>
                          <span className="text-xs text-gray-500 block">
                            {company}
                            {supplierType && ` (${supplierType})`}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="text-gray-500 hover:text-red-600"
                          onClick={() => handleRemoveMaterialSupplierPerson(person._id)}
                          aria-label={`Ta bort ${person.firstName} ${person.lastName}`}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-gray-500 text-sm mt-1">Inga materialleverantörer tillagda</p>
                )}
              </div>
            </div>
          </>
        )}
        {/* Knappar längst ner */}
        <div className="flex space-x-4 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-gray-500 text-white p-2 rounded hover:bg-gray-600 transition-colors duration-200"
          >
            Avbryt
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 bg-leadme-pink text-white p-2 rounded hover:bg-leadme-pink-dark disabled:bg-leadme-pink-light transition-colors duration-200"
          >
            {isLoading ? 'Sparar...' : 'Spara'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default UpdateProject;
